import { type PropsWithChildren } from "react"
import { useSafeContext } from "@oefr/hooks"
import {
    FEATURE_FLAG_DEFAULTS,
    type FeatureFlagKey,
    type FeatureFlagValues,
} from "@/lib/flags/feature-flags.shared"
import { createNamedContext } from "@/utils/create-named-context"

const FeatureFlagsContext = createNamedContext<FeatureFlagValues>(
    "FeatureFlagsContext",
    FEATURE_FLAG_DEFAULTS
)

type FeatureFlagsProviderProps = PropsWithChildren<{
    /**
     * Flag values resolved server-side (via `resolveFeatureFlags`) and passed
     * through page props. Missing keys fall back to `FEATURE_FLAG_DEFAULTS`.
     */
    flags?: Partial<FeatureFlagValues>
}>

/**
 * Exposes every feature flag to the whole app. Mounted once near the root, it
 * lets any component read a flag with `useFeatureFlag`/`useFeatureFlags`
 * instead of prop-drilling values down the tree.
 */
export function FeatureFlagsProvider({ children, flags }: FeatureFlagsProviderProps) {
    return (
        <FeatureFlagsContext.Provider value={{ ...FEATURE_FLAG_DEFAULTS, ...flags }}>
            {children}
        </FeatureFlagsContext.Provider>
    )
}

/** Returns every feature flag value. */
export function useFeatureFlags() {
    return useSafeContext(FeatureFlagsContext)
}

/** Returns a single feature flag value by key. */
export function useFeatureFlag<TKey extends FeatureFlagKey>(key: TKey): FeatureFlagValues[TKey] {
    return useFeatureFlags()[key]
}
